/**
 * 颜色常量统一管理
 *
 * 使用方式：
 * - 在 sx prop 中直接使用：sx={{ color: COLORS.primary.main }}
 * - 在组件中使用 useTheme()：const theme = useTheme(); theme.palette.primary.main
 *
 * 注意：优先使用 theme.palette，仅在需要非主题色时使用此文件
 */

/** 主题色映射（与 palette.ts 保持一致） */
export const COLORS = {
  primary: {
    main: '#0F9D6F',
    dark: '#0B7A56',
    light: '#E7F8F1',
    hover: '#0B8A61',
    /** 设计稿 emerald：渐变终点 / 选中 Tab 底色 */
    bright: '#10B981',
    /** 设计稿 emerald-bright：渐变起点 */
    accent: '#34D399',
    /** 主色描边（卡片/分割线） */
    line: 'rgba(16,185,129,0.16)',
    /** 主色浅底（图标底/幽灵按钮） */
    soft: 'rgba(16,185,129,0.12)',
  },
  secondary: {
    main: '#43A047',
    dark: '#2E7D32',
    light: '#E8F5E9',
  },
  error: {
    main: '#E53935',
    light: '#FFEBEE',
    dark: '#C62828',
  },
  warning: {
    main: '#FB8C00',
    light: '#FFF3E0',
    dark: '#E65100',
  },
  success: {
    main: '#43A047',
    light: '#E8F5E9',
    dark: '#2E7D32',
  },
  info: {
    main: '#00d4aa',
    light: '#E0F7F4',
    dark: '#00838F',
  },
  grey: {
    50: '#F7FAF8',
    100: '#EEF4F1',
    200: '#E3EAE6',
    300: '#D3DDD8',
    400: '#B7C3BC',
    500: '#8A9A92',
    600: '#6B7C74',
    700: '#4B5E55',
    800: '#33463D',
    900: '#10241D',
  },
  background: {
    default: '#F4F8F6',
    paper: '#FFFFFF',
    dark: '#0a1628',
  },
  text: {
    primary: '#10241D',
    secondary: '#5B6B63',
    disabled: '#8A9A92',
  },
  divider: '#E3EAE6',
} as const;

/** 设计稿通用卡片样式（圆角 16 + 浅绿描边 + 绿调阴影），首页/个人设置等卡片共用 */
export const CARD_SX = {
  borderRadius: '16px',
  border: '1px solid rgba(16,185,129,0.08)',
  boxShadow: '0 6px 22px rgba(16,80,60,0.06), 0 1px 2px rgba(16,80,60,0.04)',
  backgroundColor: '#FFFFFF',
} as const;

/** 主色渐变（主按钮 / 进度条 / 柱状图） */
export const PRIMARY_GRADIENT = 'linear-gradient(135deg, #34D399 0%, #0F9D6F 100%)';

/** 状态颜色映射（用于 StatusChip 等组件） */
export const STATUS_COLORS = {
  success: COLORS.success.main,
  warning: COLORS.warning.main,
  error: COLORS.error.main,
  info: COLORS.info.main,
  default: COLORS.grey[500],
} as const;

/** 图表颜色（用于 Recharts 等） */
export const CHART_COLORS = {
  /** 历史命名：blue = 品牌主色（现为翡翠绿） */
  blue: COLORS.primary.main,
  primary: COLORS.primary.main,
  green: COLORS.success.main,
  orange: COLORS.warning.main,
  red: COLORS.error.main,
  teal: COLORS.info.main,
  purple: '#7B1FA2',
  pink: '#E91E63',
  indigo: '#3F51B5',
} as const;
